import { normalizeUserResponse } from "../../../user/api";
import { defaultFetchHeaders, fetchJson, normalizeIdentityEmail } from "../../../utils";
import { userType } from "../../../user/types";
import { LocationContact, OperatorUser, OrganizationMember, PendingInvite } from "../../types";
import { SuperuserOrganizationOption, SuperuserSupportAccount } from "../../state/superuserContext";
import {
  assignableOrganizationMembers,
  isAssignableOrganizationMember,
  normalizeOrganizationMember,
  organizationMemberStatusLabel,
  organizationMemberUserId,
  organizationMemberToOperator,
  organizationMemberRoleToApi,
} from "../../organizationMember";
import { suppressRealtimeEvent } from "../../realtime/suppressRealtime";

const backend = process.env.REACT_APP_BACKEND_URL || "";

const normalizeSuperuserOrganizationOption = (
  organization: any
): SuperuserOrganizationOption => ({
  id: String(organization?.id || ""),
  title: String(organization?.title || "Untitled organization"),
  ownerEmail: organization?.ownerEmail ?? organization?.owner_email ?? null,
  logoUrl: organization?.logoUrl ?? organization?.logo_url ?? null,
  useDefaultLogo:
    organization?.useDefaultLogo ?? organization?.use_default_logo ?? true,
});

type OrganizationInviteResponse = {
  inviteeEmail?: string | null;
  organizationId: string;
  role: string;
  locationId?: string | null;
  inviterId: string;
  status: string;
  revokedAt?: string | null;
  acceptedAt?: string | null;
  expiresAt: string;
  inviteUrl?: string | null;
  userExists: boolean;
  hasPassword: boolean;
  lastName?: string | null;
  firstName?: string | null;
  targetType: "organization_user" | "location_contact";
};

type InviteAcceptResponse = {
  organizationId?: string | null;
  locationId?: string | null;
  status?: string;
  targetType?: "organization_user" | "location_contact";
};

const normalizePendingInvite = (invite: any): PendingInvite => ({
  inviteId: invite?.inviteId ?? invite?.invite_id ?? "",
  targetType: invite?.targetType ?? invite?.target_type ?? "location_contact",
  organizationId: invite?.organizationId ?? invite?.organization_id ?? "",
  organizationName: invite?.organizationName ?? invite?.organization_name ?? "",
  locationId: invite?.locationId ?? invite?.location_id ?? "",
  locationName:
    invite?.locationName ??
    invite?.location_name ??
    "",
  role: invite?.role,
});

const normalizeInviteResponse = (invite: any): OrganizationInviteResponse => ({
  inviteeEmail: invite?.inviteeEmail ?? invite?.invitee_email ?? null,
  organizationId: invite?.organizationId ?? invite?.organization_id ?? "",
  role: invite?.role ?? "",
  locationId: invite?.locationId ?? invite?.location_id ?? null,
  inviterId: invite?.inviterId ?? invite?.inviter_id ?? "",
  status: invite?.status ?? "",
  revokedAt: invite?.revokedAt ?? invite?.revoked_at ?? null,
  acceptedAt: invite?.acceptedAt ?? invite?.accepted_at ?? null,
  expiresAt: invite?.expiresAt ?? invite?.expires_at ?? "",
  inviteUrl: invite?.inviteUrl ?? invite?.invite_url ?? null,
  userExists: Boolean(invite?.userExists ?? invite?.user_exists),
  hasPassword: Boolean(invite?.hasPassword ?? invite?.has_password),
  lastName: invite?.lastName ?? invite?.last_name ?? null,
  firstName: invite?.firstName ?? invite?.first_name ?? null,
  targetType: invite?.targetType ?? invite?.target_type ?? "organization_user",
});

const extractOrganizationMembers = (payload: any): OrganizationMember[] => {
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.members)
    ? payload.members
    : [];
  return list.map(normalizeOrganizationMember);
};

const accountName = (account: {
  firstName?: string | null;
  first_name?: string | null;
  lastName?: string | null;
  last_name?: string | null;
  email?: string | null;
  invitee_email?: string | null;
}) => {
  const first = account.firstName ?? account.first_name ?? "";
  const last = account.lastName ?? account.last_name ?? "";
  const name = [first, last].filter(Boolean).join(" ").trim();
  return name || account.email || account.invitee_email || "Account";
};

const normalizeSupportUser = (account: any): userType => normalizeUserResponse({
  ...account,
  email: account.email ?? "",
  isAuthenticated: account.isAuthenticated ?? account.is_authenticated ?? true,
});

const normalizeLocationContact = (contact: any): LocationContact => ({
  id: contact?.id,
  userId: contact?.uid ?? contact?.userId ?? contact?.user_id,
  active: contact?.active,
  inviteStatus: contact?.inviteStatus ?? contact?.invite_status,
  firstName: contact?.firstName ?? contact?.first_name,
  lastName: contact?.lastName ?? contact?.last_name,
  email: contact?.email,
  phone: contact?.phone,
  role: contact?.role,
});

export const supportAccountFromMember = (
  member: OrganizationMember,
): SuperuserSupportAccount | null => {
  const status = member.status || "none";
  const userId = organizationMemberUserId(member);
  const id = userId || member.id || "";
  if (!id) return null;
  const canSelect = isAssignableOrganizationMember(member) || Boolean(userId);
  const disabledReason = canSelect
    ? status === "pending"
      ? "Invite pending"
      : null
    : status === "pending"
      ? "Invite pending - account not created"
      : organizationMemberStatusLabel(status);
  return {
    id,
    label: accountName(member),
    email: member.invitee_email || member.inviteeEmail || "",
    role: member.role,
    source: "member",
    status,
    canSelect,
    disabledReason,
  };
};

export const supportAccountFromLocationContact = (
  contact: LocationContact,
): SuperuserSupportAccount | null => {
  const id = contact.userId || contact.id || "";
  if (!id) return null;
  const inviteStatus = contact.inviteStatus || null;
  const invitePending = inviteStatus?.toLowerCase().includes("pending") === true;
  const canSelect = Boolean(contact.userId) && (contact.active !== false || invitePending);
  const disabledReason = canSelect
    ? invitePending
      ? "Invite pending"
      : null
    : invitePending
      ? "Invite pending - account not created"
      : "Account unavailable";
  return {
    id,
    label: accountName(contact),
    email: contact.email || "",
    role: contact.role || "staff",
    source: "location_contact",
    status: inviteStatus || (contact.active === false ? "Inactive" : "Active"),
    canSelect,
    disabledReason,
  };
};

export async function fetchOperators(): Promise<OperatorUser[]> {
  const payload = await fetchJson<any>(
    `${backend}/user/organization/members`,
    {
      method: "GET",
      credentials: "include",
      headers : await defaultFetchHeaders(),
    },
    "Failed to fetch organization members"
  );
  const members = extractOrganizationMembers(payload);
  return assignableOrganizationMembers(members)
    .map(organizationMemberToOperator)
    .filter((member): member is OperatorUser => Boolean(member));
}

export async function inviteLocationContact(payload: {
  locationId: string;
  email: string;
  role?: string;
  firstName?: string;
  lastName?: string;
  phone?: string | null;
}): Promise<OrganizationInviteResponse> {
  const invite = await fetchJson(
    `${backend}/user/invite/location-contact`,
    {
      method: "POST",
      credentials: "include",
      headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
      body: JSON.stringify({
        locationId: payload.locationId,
        email: normalizeIdentityEmail(payload.email),
        role: payload.role || "staff",
        firstName: payload.firstName || undefined,
        lastName: payload.lastName || undefined,
        phone: payload.phone ?? undefined,
      }),
    },
    "Failed to send contact invite"
  );
  return normalizeInviteResponse(invite);
}

export async function getLocationContactQrInvite(payload: {
  locationId: string;
  role: string;
  fresh?: boolean;
}): Promise<OrganizationInviteResponse> {
  const invite = await fetchJson(
    `${backend}/user/invite/location-contact/qr?fresh=${payload.fresh ? "true" : "false"}`,
    {
      method: "POST",
      credentials: "include",
      headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
      body: JSON.stringify({
        locationId: payload.locationId,
        role: payload.role || "owner",
      }),
    },
    "Failed to load QR invite"
  );
  return normalizeInviteResponse(invite);
}

export async function inviteOrganizationUser(payload: {
  email: string;
  role: string;
  firstName?: string;
  lastName?: string;
}): Promise<OrganizationInviteResponse> {
  suppressRealtimeEvent("invite.changed");
  const invite = await fetchJson(
    `${backend}/user/invite/organization`,
    {
      method: "POST",
      credentials: "include",
      headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
      body: JSON.stringify({
        email: normalizeIdentityEmail(payload.email),
        role: organizationMemberRoleToApi(payload.role),
        firstName: payload.firstName || undefined,
        lastName: payload.lastName || undefined,
      }),
    },
    "Failed to send organization invite"
  );
  return normalizeInviteResponse(invite);
}

export async function getOrganizationQrInvite(payload: {
  role: string;
  fresh?: boolean;
}): Promise<OrganizationInviteResponse> {
  suppressRealtimeEvent("invite.changed");
  const invite = await fetchJson(
    `${backend}/user/invite/organization/qr?fresh=${payload.fresh ? "true" : "false"}`,
    {
      method: "POST",
      credentials: "include",
      headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
      body: JSON.stringify({
        role: organizationMemberRoleToApi(payload.role),
      }),
    },
    "Failed to load QR invite"
  );
  return normalizeInviteResponse(invite);
}

export async function fetchOrganizationMembers(): Promise<OrganizationMember[]> {
  const payload = await fetchJson<any>(
    `${backend}/user/organization/members`,
    {
      method: "GET",
      credentials: "include",
      headers : await defaultFetchHeaders(),
    },
    "Failed to fetch organization members"
  );
  return extractOrganizationMembers(payload);
}

export async function fetchOrganizationLocationContacts(): Promise<LocationContact[]> {
  const payload = await fetchJson<any[]>(
    `${backend}/user/organization/location-contacts`,
    {
      method: "GET",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to fetch location contacts"
  );
  return Array.isArray(payload) ? payload.map(normalizeLocationContact) : [];
}

export async function fetchSupportAccount(userId: string): Promise<userType> {
  const payload = await fetchJson<any>(
    `${backend}/user/organization/members/account?userId=${encodeURIComponent(userId)}`,
    {
      method: "GET",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to fetch support account"
  );
  return normalizeSupportUser(payload);
}

export async function updateSupportAccount(
  userId: string,
  payload: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    sidebarRestoreLastVisited?: boolean;
    sidebarShowRememberedShortcuts?: boolean;
  }
): Promise<userType> {
  const response = await fetchJson<any>(
    `${backend}/user/organization/members/account/edit?userId=${encodeURIComponent(userId)}`,
    {
      method: "POST",
      credentials: "include",
      headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    "Failed to update support account"
  );
  return normalizeSupportUser(response);
}

export async function sendSupportPasswordReset(userId: string): Promise<void> {
  await fetchJson(
    `${backend}/user/organization/members/password/reset?userId=${encodeURIComponent(userId)}`,
    {
      method: "POST",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to send password reset"
  );
}

export async function updateOrganizationMemberRole(
  userId: string,
  role: string
): Promise<void> {
  const normalizedRole = organizationMemberRoleToApi(role);
  await fetchJson(
    `${backend}/user/organization/members/role?userId=${encodeURIComponent(userId)}&role=${encodeURIComponent(normalizedRole)}`,
    {
      method: "POST",
      credentials: "include",
      headers : await defaultFetchHeaders(),
    },
    "Failed to update member role"
  );
}

export async function removeOrganizationMember(userId: string): Promise<void> {
  await fetchJson(
    `${backend}/user/organization/members/remove?userId=${encodeURIComponent(userId)}`,
    {
      method: "POST",
      credentials: "include",
      headers : await defaultFetchHeaders(),
    },
    "Failed to remove member"
  );
}

export async function revokeOrganizationInvite(
  userId: string
): Promise<void> {
  suppressRealtimeEvent("invite.changed");
  await fetchJson(
    `${backend}/user/organization/members/revoke?userId=${encodeURIComponent(userId)}`,
    {
      method: "POST",
      credentials: "include",
      headers : await defaultFetchHeaders(),
    },
    "Failed to revoke invite"
  );
}

export async function fetchPendingInvites(): Promise<PendingInvite[]> {
  const payload = await fetchJson<any[]>(
    `${backend}/user/invite/pending`,
    {
      method: "GET",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to fetch pending invites"
  );
  return Array.isArray(payload) ? payload.map(normalizePendingInvite) : [];
}

async function respondToAuthenticatedInvite(
  inviteId: string, decision: "accept-authenticated" | "decline"
): Promise<InviteAcceptResponse> {
  suppressRealtimeEvent("invite.changed");
  const result = await fetchJson<any>(
    `${backend}/user/invite/${decision}`,
    {
      method: "POST",
      credentials: "include",
      headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
      body: JSON.stringify({ inviteId }),
    },
    decision === "decline" ? "Failed to decline invite" : "Failed to accept invite"
  );
  return { organizationId: result.organizationId ?? result.organization_id ?? null,
    locationId: result.locationId ?? result.location_id ?? null,
    status: result.status, targetType: result.targetType ?? result.target_type };
}

export const acceptAuthenticatedInvite = (inviteId: string) => respondToAuthenticatedInvite(inviteId, "accept-authenticated");
export const declineAuthenticatedInvite = (inviteId: string) => respondToAuthenticatedInvite(inviteId, "decline");

export async function fetchSuperuserOrganizations(payload: {
  q?: string;
  limit?: number;
  offset?: number;
} = {}, signal?: AbortSignal): Promise<SuperuserOrganizationOption[]> {
  const params = new URLSearchParams();
  const q = payload.q?.trim();
  if (q) params.set("q", q);
  if (payload.limit !== undefined) params.set("limit", String(payload.limit));
  if (payload.offset !== undefined) params.set("offset", String(payload.offset));
  const query = params.toString();
  const organizations = await fetchJson<any>(
    `${backend}/user/superuser/organizations${query ? `?${query}` : ""}`,
    {
      method: "GET",
      credentials: "include",
      headers: await defaultFetchHeaders(),
      signal,
    },
    "Failed to fetch organizations"
  );
  const items = Array.isArray(organizations)
    ? organizations
    : Array.isArray((organizations as any)?.items)
      ? (organizations as any).items
      : [];
  return items
    .map(normalizeSuperuserOrganizationOption)
    .filter((organization: SuperuserOrganizationOption) => organization.id);
}


export async function updateCommissionApprovalAccess(userId: string, enabled: boolean, signal?: AbortSignal): Promise<{ canApproveCommissionPayments: boolean }> {
  return fetchJson(`${backend}/user/organization/members/commission-approval?${new URLSearchParams({ userId, enabled: String(enabled) })}`, {
    method: "POST", credentials: "include", headers: await defaultFetchHeaders(), signal,
  }, "Unable to update commission approval access");
}


export async function updateExpenseAssociationAccess(userId: string, enabled: boolean, signal?: AbortSignal): Promise<{ canLinkExpenseAssociations: boolean }> {
  return fetchJson(`${backend}/user/organization/members/expense-associations?${new URLSearchParams({ userId, enabled: String(enabled) })}`, {
    method: "POST", credentials: "include", headers: await defaultFetchHeaders(), signal,
  }, "Unable to update expense association access");
}
